import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const API_KEY = "AIzaSyDDv9FFGFzMbPDsaPgRF8rIh5RP12uB98I";
const CALENDAR_ID =
  "1325b8c6067825f0531ccfe2635bb3d0fdd717e82fae00312289e78f06e5e7b0@group.calendar.google.com";

export default function WeekViewCalendar({ currentWeek, setCurrentDate }) {
  const [events, setEvents] = useState([]);
  const scrollRef = useRef(null);
  const hours = Array.from({ length: 24 }, (_, i) => i); // 0h à 23h

  useEffect(() => {
    fetchEvents();
    scrollToCurrentTime();
  }, [currentWeek]);

  const fetchEvents = async () => {
    const startOfWeek = currentWeek.startOf("week").toISOString();
    const endOfWeek = currentWeek.endOf("week").toISOString();

    const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
      CALENDAR_ID
    )}/events?key=${API_KEY}&timeMin=${startOfWeek}&timeMax=${endOfWeek}&singleEvents=true&orderBy=startTime`;

    try {
      const response = await axios.get(url);
      setEvents(response.data.items);
    } catch (error) {
      console.error("Erreur lors du chargement des événements", error);
    }
  };

  const getEventsForDay = (date) => {
    return events.filter((event) =>
      dayjs(event.start.dateTime || event.start.date).isSame(date, "day")
    );
  };

  const scrollToCurrentTime = () => {
    const now = dayjs();
    const offset = (now.hour() + now.minute() / 60) * 64;
    if (scrollRef.current) {
      scrollRef.current.scrollTop = offset - 128; // scroll un peu avant
    }
  };

  const now = dayjs();
  const isCurrentWeek = currentWeek.isSame(now, "week");
  const nowTop = (now.hour() + now.minute() / 60) * 64;

  return (
    <div
      className="overflow-auto border rounded-lg shadow bg-white h-[760px]"
      ref={scrollRef}
    >
      {/* En-tête des jours */}
      <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b bg-gray-50 sticky top-0 z-10">
        <div className="w-[80px] border-r" />
        {[...Array(7)].map((_, idx) => {
            const date = currentWeek.startOf("week").add(idx, "day");
            const isToday = date.isSame(dayjs(), "day");

            return (
            <div
                key={idx}
                className={`text-center py-2 text-sm font-semibold relative flex items-center justify-center gap-2 h-full border-r ${
                idx === 0 ? "border-l" : ""
                } ${isToday ? "bg-blue-50 border border-blue-400 rounded" : ""}`}
            >
                <span>{date.format("ddd")}</span>
                <span
                className={`w-7 h-7 text-sm leading-7 text-center rounded-full ${
                    isToday ? "bg-blue-600 text-white" : ""
                }`}
                >
                {date.format("D")}
                </span>
            </div>
            );
        })}
      </div>

      {/* Grille horaire */}
      <div className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] relative text-xs">
        {/* Colonne des heures */}
        <div className="w-[80px] border-r text-right pr-3 pt-2 bg-white">
            {hours.map((h, idx) => (
                <div
                key={idx}
                className="h-16 flex items-center justify-end pr-2 text-sm text-gray-600"
                >
                {dayjs().hour(h).minute(0).format("h A")}
                </div>
            ))}
        </div>


        {/* Colonnes des jours */}
        {[...Array(7)].map((_, dayIdx) => {
          const date = currentWeek.startOf("week").add(dayIdx, "day");
          const isToday = date.isSame(now, "day");
          const dayEvents = getEventsForDay(date);

          return (
            <div
              key={dayIdx}
              className={`border-r relative ${
                isToday ? "bg-blue-50" : "bg-white"
              }`}
              style={{ minHeight: hours.length * 64 }}
            >
              {/* Grille horizontale */}
              {hours.map((_, hIdx) => (
                <div key={hIdx} className="h-16 border-b border-gray-200" />
              ))}

              {/* LIGNE ROUGE : Heure actuelle */}
              {isCurrentWeek && isToday && (
                <div
                  className="absolute left-0 right-0 h-0.5 bg-red-500 z-20"
                  style={{ top: nowTop }}
                />
              )}

              {/* Événements positionnés */}
              {dayEvents.map((event, i) => {
                const start = dayjs(event.start.dateTime);
                const end = dayjs(event.end.dateTime);
                const top =
                  (start.hour() + start.minute() / 60) * 64;
                const height =
                  (end.diff(start, "minute") / 60) * 64 || 64;

                return (
                  <div
                    key={i}
                    className={`absolute left-1 right-1 px-2 py-1 text-xs rounded shadow ${
                      i % 2 === 0
                        ? "bg-blue-100 text-blue-800"
                        : "bg-pink-100 text-pink-800"
                    }`}
                    style={{ top, height }}
                  >
                    <div className="font-semibold text-[10px]">
                      {start.format("h:mm A")}
                    </div>
                    <div className="truncate">{event.summary}</div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
